import {
  Dispatch,
  FC,
  Fragment,
  memo,
  MouseEvent,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import { UserContext } from 'src/app/providers/UserProvider';
import {
  BulkCreateFormsPreviewRes,
  BulkGenerateFormsParams,
  CreateFormParams,
  CreateFormRes,
  DirectoryAttributeItem,
  ProjectDirectoryItem,
} from 'src/shared/api/projects';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { ReactComponent as DirectoryIcon } from 'src/shared/assets/icons/kit/dictionary.svg';
import { ReactComponent as TrashIcon } from 'src/shared/assets/icons/kit/trash.svg';
import { emptyError } from 'src/shared/const';
import {
  setError as setGlobalError,
  setSuccess as setGlobalSuccess,
  transliterate,
} from 'src/shared/lib/utils';
import {
  FormBusinessRule,
  FormFlkRule,
  FormTotalsRowPosition,
  FormTotalsRowSettings,
  FormType,
  FormViewDependencyOperator,
  FormViewDependencyRule,
  TableSettingsForm,
} from 'src/shared/store/projects/types';
import { tableContextDefaultState } from 'src/shared/store/table/constants';
import { Button, ButtonProps, Input, Select, SelectItemType, ToolTip } from 'src/shared/ui';

import { FieldType, LocalFieldType, ProjectSettingsFormType } from '../types';

import { BusinessRulesBuilder } from './BusinessRulesBuilder';
import { FlkRulesBuilder } from './FlkRulesBuilder';
import { TotalsRowViewSettings } from './TotalsRowViewSettings';

interface ProjectSettingsFormProps {
  onClose: () => void;
  setDirectoryModal: Dispatch<SetStateAction<number | null | undefined>>;
  setLocalFields: Dispatch<SetStateAction<LocalFieldType[]>>;
  localFields: LocalFieldType[];
  settings?: TableSettingsForm;
}

const defaultField = {
  removed: false,
};

type BulkDimensionDraft = {
  id: number;
  key: string;
  directoryFormId: number | '';
  valueColumn: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
};

type DependentDisplayRuleDraft = {
  id: string;
  targetColumn: string;
  sourceColumn: string;
  operator: FormViewDependencyOperator;
  valuesInput: string;
  enabled: boolean;
};

const buildDefaultBulkDimension = (id: number): BulkDimensionDraft => ({
  id,
  key: `dim${id}`,
  directoryFormId: '',
  valueColumn: 'id',
  sortColumn: 'id',
  sortDirection: 'asc',
});

const parseNumberInput = (value: string): number | undefined => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return Math.trunc(parsed);
};

const buildDependentDisplayRuleDraft = (index: number): DependentDisplayRuleDraft => ({
  id: `dep-rule-${index}`,
  targetColumn: '',
  sourceColumn: '',
  operator: 'in',
  valuesInput: '',
  enabled: true,
});

const dependentRuleOperators: Array<{ value: FormViewDependencyOperator; label: string }> = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'in', label: 'В списке' },
  { value: 'not_in', label: 'Не в списке' },
  { value: 'is_empty', label: 'Пусто' },
  { value: 'not_empty', label: 'Не пусто' },
];

export const ProjectSettingsForm: FC<ProjectSettingsFormProps> = memo(
  ({ onClose, setDirectoryModal, setLocalFields, localFields, settings }) => {
    const {
      register,
      handleSubmit,
      control,
      setValue,
      getValues,
      setError,
      clearErrors,
      watch,
      formState: { errors, isSubmitting },
    } = useForm<ProjectSettingsFormType>({
      mode: 'all',
      defaultValues: {
        bulkFormNames: '',
        appearanceSettings: {
          preset: 'default',
          primaryColor: '#1E2A5A',
          backgroundColor: '#FFFFFF',
          fontFamily: 'GothamPro',
          fontSize: 14,
        },
        viewSettings: {
          hideEmptyCells: false,
          hideReferencedValues: false,
          businessRulesStopOnError: true,
        },
      },
    });
    const { darkMode } = useContext(UserContext);
    const { setTableSettings, setSelectedForm, forms, setForms, selectedProject } =
      useContext(ProjectsContext);
    const { setTableData } = useContext(TableContext);
    const [businessRules, setBusinessRules] = useState<FormBusinessRule[]>([]);
    const [flkRules, setFlkRules] = useState<FormFlkRule[]>([]);
    const [bulkGenerationEnabled, setBulkGenerationEnabled] = useState<boolean>(false);
    const [bulkDimensions, setBulkDimensions] = useState<BulkDimensionDraft[]>([
      buildDefaultBulkDimension(1),
    ]);
    const [bulkNameTemplate, setBulkNameTemplate] = useState<string>('{{value}}');
    const [bulkDescriptionTemplate, setBulkDescriptionTemplate] = useState<string>('{{value}}');
    const [bulkInstanceLimit, setBulkInstanceLimit] = useState<string>('100');
    const [bulkPreviewLimit, setBulkPreviewLimit] = useState<string>('50');
    const [bulkPreview, setBulkPreview] = useState<BulkCreateFormsPreviewRes | null>(null);
    const [isBulkPreviewLoading, setIsBulkPreviewLoading] = useState<boolean>(false);
    const [dependentDisplayRules, setDependentDisplayRules] = useState<DependentDisplayRuleDraft[]>(
      [],
    );
    const [projectDirectories, setProjectDirectories] = useState<ProjectDirectoryItem[]>([]);
    const [directoryAttributes, setDirectoryAttributes] = useState<
      Record<number, DirectoryAttributeItem[]>
    >({});
    const watchedFields = watch('fields');
    const watchedTotalsRowSettings = watch('viewSettings.totalsRow');
    const availableColumns = useMemo(
      () =>
        Array.from(
          new Set(
            (watchedFields ?? [])
              .map((field, index) => {
                if (localFields?.[index]?.removed) {
                  return '';
                }
                return (field?.columnName || '').trim();
              })
              .filter(Boolean),
          ),
        ),
      [localFields, watchedFields],
    );
    const availableTargetTables = useMemo(
      () =>
        Array.from(
          new Set(
            [settings?.connection || '', ...forms.map(form => form.form_name || '')]
              .map(value => value.trim())
              .filter(Boolean),
          ),
        ),
      [forms, settings?.connection],
    );
    const loadDirectoryAttributes = useCallback(
      async (directoryId: number) => {
        if (!directoryId || directoryAttributes[directoryId]) {
          return;
        }
        try {
          const { data } = await ProjectsService.getDirectoryAttributes(directoryId);
          setDirectoryAttributes(prev => ({ ...prev, [directoryId]: data?.items || [] }));
        } catch (error) {
          console.error('Failed to load directory attributes', error);
        }
      },
      [directoryAttributes],
    );

    const updateTotalsRowViewSettings = useCallback(
      (patch: Partial<FormTotalsRowSettings>) => {
        const currentTotalsRow = getValues('viewSettings.totalsRow');
        const nextTotalsRow: FormTotalsRowSettings = {
          enabled: currentTotalsRow?.enabled ?? false,
          label: currentTotalsRow?.label ?? 'Итоги',
          position: currentTotalsRow?.position ?? 'end',
          columns: currentTotalsRow?.columns ?? [],
          ...patch,
        };

        setValue('viewSettings.totalsRow', nextTotalsRow, {
          shouldDirty: true,
        });
      },
      [getValues, setValue],
    );

    useEffect(() => {
      if (settings || !selectedProject?.id || !bulkGenerationEnabled) {
        return;
      }

      const loadDirectories = async () => {
        try {
          const { data } = await ProjectsService.getProjectDirectories(selectedProject.id);
          setProjectDirectories(data?.directories || []);
        } catch (error) {
          console.error('Failed to load project directories', error);
          setGlobalError('Не удалось загрузить измерения проекта');
        }
      };

      void loadDirectories();
    }, [bulkGenerationEnabled, selectedProject?.id, settings]);

    useEffect(() => {
      if (!bulkGenerationEnabled) {
        return;
      }
      bulkDimensions.forEach(dimension => {
        if (typeof dimension.directoryFormId === 'number') {
          void loadDirectoryAttributes(dimension.directoryFormId);
        }
      });
    }, [bulkDimensions, bulkGenerationEnabled, loadDirectoryAttributes]);

    const updateBulkDimension = (id: number, patch: Partial<BulkDimensionDraft>) => {
      setBulkDimensions(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
    };

    const addBulkDimension = () => {
      setBulkDimensions(prev => {
        const nextId = prev.reduce((maxId, item) => Math.max(maxId, item.id), 0) + 1;
        return [...prev, buildDefaultBulkDimension(nextId)];
      });
    };

    const removeBulkDimension = (id: number) => {
      setBulkDimensions(prev => {
        if (prev.length <= 1) {
          return prev;
        }
        return prev.filter(item => item.id !== id);
      });
    };

    const getTypeOptions = (i: number): SelectItemType[] => [
      { value: 'text', label: 'Текст' },
      { value: 'number', label: 'Целое число' },
      { value: 'floatNumber', label: 'Дробное число' },
      { value: 'boolean', label: 'Логический' },
      { value: 'date', label: 'Дата' },
      {
        value: 'reference',
        label: 'Справочник',
        icon: (
          <DirectoryIcon
            className="h-4 w-4 fill-white hover:fill-blue-300"
            onClick={e => {
              e.stopPropagation();
              setDirectoryModal(i + 1);
              clearErrors(`fields.${i}.columnType`);
            }}
          />
        ),
      },
    ];
    const appearancePresetOptions: SelectItemType[] = [
      { value: 'default', label: 'Стандартный' },
      { value: 'compact', label: 'Компактный' },
      { value: 'contrast', label: 'Контрастный' },
      { value: 'custom', label: 'Пользовательский' },
    ];

    useEffect(() => {
      if (!settings) {
        setBusinessRules([]);
        setFlkRules([]);
        setBulkGenerationEnabled(false);
        setBulkDimensions([buildDefaultBulkDimension(1)]);
        setBulkNameTemplate('{{value}}');
        setBulkDescriptionTemplate('{{value}}');
        setBulkInstanceLimit('100');
        setBulkPreviewLimit('50');
        setBulkPreview(null);
        setDependentDisplayRules([]);
        return handleAddRow();
      }
      setValue('formName', settings.formName);
      setValue('formDescription', settings.formDescription);
      setValue('bulkFormNames', '');
      setBusinessRules(settings?.businessRules || []);
      setFlkRules(settings?.flkRules || []);
      setValue('appearanceSettings', {
        preset: settings?.appearanceSettings?.preset || 'default',
        primaryColor: settings?.appearanceSettings?.primaryColor || '#1E2A5A',
        backgroundColor: settings?.appearanceSettings?.backgroundColor || '#FFFFFF',
        fontFamily: settings?.appearanceSettings?.fontFamily || 'GothamPro',
        fontSize: settings?.appearanceSettings?.fontSize || 14,
        customCss: settings?.appearanceSettings?.customCss || '',
      });
      setValue('viewSettings', {
        hideEmptyCells: Boolean(settings?.viewSettings?.hideEmptyCells),
        hideReferencedValues: Boolean(settings?.viewSettings?.hideReferencedValues),
        businessRulesStopOnError:
          settings?.viewSettings?.businessRulesStopOnError === undefined
            ? true
            : Boolean(settings?.viewSettings?.businessRulesStopOnError),
        dependentDisplayRules: settings?.viewSettings?.dependentDisplayRules || [],
        totalsRow: settings?.viewSettings?.totalsRow,
      });
      setDependentDisplayRules(
        (settings?.viewSettings?.dependentDisplayRules || []).map((rule, index) => ({
          id: rule.id || `dep-rule-${index + 1}`,
          targetColumn: rule.targetColumn || '',
          sourceColumn: rule.sourceColumn || '',
          operator: rule.operator || 'in',
          valuesInput: (rule.values || []).map(value => String(value)).join(', '),
          enabled: rule.enabled !== false,
        })),
      );
      settings.formStructure.forEach((row, i) => {
        handleAddRow(row, i);
      });
    }, [settings]);

    const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onClose();
    };

    const handleAddRow = (row?: FieldType, i?: number) => {
      setLocalFields(prev => [
        ...prev,
        {
          ...defaultField,
          isNew: !row,
          ...(row?.referenceFormId && {
            dictionary: {
              referenceFormValue: row?.referenceFormValue,
              referenceFormId: row?.referenceFormId,
              referenceFormKey: row?.referenceFormKey,
            },
          }),
        },
      ]);
      const index = i || localFields.length;
      setValue(`fields.${index}.columnType`, row?.columnType || 'text');

      if (row) {
        setValue(`fields.${index}.columnName`, row.columnName);
        setValue(`fields.${index}.columnLabel`, row.columnLabel);
      }
    };

    const handleRemoveRow = (e: MouseEvent<HTMLButtonElement>, i: number) => {
      e.preventDefault();
      setLocalFields(prev =>
        prev.map((el, index) => ({ ...el, ...(i === index && { removed: true }) })),
      );
    };

    const checkNames = (fields: FieldType[]) => {
      return fields.reduce((sum, cur) => {
        if (sum[cur.columnName]) {
          sum[cur.columnName] += 1;
        } else {
          sum[cur.columnName] = 1;
        }

        return sum;
      }, {});
    };

    const mapResponseToForm = (formResponse: CreateFormRes): FormType => ({
      id: formResponse.Id || formResponse?.['ID'],
      form_name: formResponse.Alias || '',
      form_description: formResponse.FormDescription,
    });

    const parseBulkFormNames = (bulkValue: string | undefined, fallbackName: string): string[] => {
      const names = (bulkValue || '')
        .split(/\r?\n/g)
        .map(item => item.trim())
        .filter(Boolean);
      const withFallback = names.length ? names : [fallbackName.trim()];
      return Array.from(new Set(withFallback.filter(Boolean)));
    };

    const parseDependentRuleValue = (rawValue: string): string | number | boolean => {
      const normalized = rawValue.trim();
      if (!normalized) {
        return '';
      }
      const lower = normalized.toLowerCase();
      if (lower === 'true') {
        return true;
      }
      if (lower === 'false') {
        return false;
      }
      if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
        return Number(normalized);
      }
      return normalized;
    };

    const operatorNeedsValues = (operator: FormViewDependencyOperator): boolean =>
      operator !== 'is_empty' && operator !== 'not_empty';

    const buildDependentDisplayRulesPayload = (): FormViewDependencyRule[] => {
      const result = dependentDisplayRules
        .filter(rule => rule.targetColumn.trim() && rule.sourceColumn.trim())
        .map(rule => {
          const values = rule.valuesInput
            .split(/[,;\n]/g)
            .map(value => value.trim())
            .filter(Boolean)
            .map(parseDependentRuleValue);

          if (rule.targetColumn.trim().toLowerCase() === rule.sourceColumn.trim().toLowerCase()) {
            throw new Error(
              'Правило зависимого отображения не может ссылаться на одну и ту же исходную и целевую колонку',
            );
          }
          if (operatorNeedsValues(rule.operator) && !values.length) {
            throw new Error(
              `Для правила зависимого отображения "${rule.targetColumn}" укажите значения для оператора "${rule.operator}"`,
            );
          }

          return {
            id: rule.id,
            targetColumn: rule.targetColumn.trim(),
            sourceColumn: rule.sourceColumn.trim(),
            operator: rule.operator,
            values: operatorNeedsValues(rule.operator) ? values : [],
            enabled: rule.enabled,
          } as FormViewDependencyRule;
        });

      return result;
    };

    const updateDependentDisplayRule = (id: string, patch: Partial<DependentDisplayRuleDraft>) => {
      setDependentDisplayRules(prev =>
        prev.map(rule => (rule.id === id ? { ...rule, ...patch } : rule)),
      );
    };

    const addDependentDisplayRule = () => {
      setDependentDisplayRules(prev => [...prev, buildDependentDisplayRuleDraft(prev.length + 1)]);
    };

    const removeDependentDisplayRule = (id: string) => {
      setDependentDisplayRules(prev => prev.filter(rule => rule.id !== id));
    };

    const resolveFormStructureWithReferences = (fields: FieldType[]): FieldType[] | null => {
      let hasError = false;
      const resolved = fields.map((field, i) => {
        if (field.columnType === 'reference') {
          if (localFields?.[i]?.dictionary) {
            return { ...field, ...localFields[i].dictionary };
          }
          hasError = true;
          setError(`fields.${i}.columnType`, { message: emptyError });
        }
        return field;
      });

      if (hasError) {
        return null;
      }

      return resolved;
    };

    const resolveActiveFormStructure = (fields: FieldType[]): FieldType[] | null => {
      const resolved = resolveFormStructureWithReferences(fields);
      if (!resolved) {
        return null;
      }
      return resolved.filter((_, i) => !localFields[i].removed);
    };

    const buildBulkGenerationPayload = (
      basePayload: Omit<CreateFormParams, 'formName' | 'formDescription'>,
      fallbackFormName: string,
      fallbackDescription: string,
    ): BulkGenerateFormsParams => {
      const dimensions = bulkDimensions
        .map((dimension, index) => {
          if (typeof dimension.directoryFormId !== 'number') {
            return null;
          }

          const key = (dimension.key || '').trim() || `dim${index + 1}`;
          return {
            key,
            directoryFormId: dimension.directoryFormId,
            valueColumn: (dimension.valueColumn || 'id').trim(),
            sortColumn: (dimension.sortColumn || dimension.valueColumn || 'id').trim(),
            sortDirection: dimension.sortDirection || 'asc',
          };
        })
        .filter(Boolean) as BulkGenerateFormsParams['dimensions'];

      if (!dimensions.length) {
        throw new Error('Добавьте хотя бы одно измерение для генерации');
      }

      const uniqueKeys = new Set<string>();
      dimensions.forEach(dimension => {
        if (uniqueKeys.has(dimension.key)) {
          throw new Error(`Ключ измерения повторяется: ${dimension.key}`);
        }
        uniqueKeys.add(dimension.key);
      });

      const preparedNameTemplate = (bulkNameTemplate || '').trim() || '{{value}}';
      const preparedDescriptionTemplate =
        (bulkDescriptionTemplate || '').trim() || fallbackDescription || '{{value}}';

      return {
        baseForm: {
          ...basePayload,
          formName: fallbackFormName.trim() || 'Сгенерированная форма',
          formDescription:
            fallbackDescription.trim() || fallbackFormName.trim() || 'Сгенерированная форма',
        },
        dimensions,
        nameTemplate: preparedNameTemplate,
        descriptionTemplate: preparedDescriptionTemplate,
        instanceLimit: parseNumberInput(bulkInstanceLimit),
        previewLimit: parseNumberInput(bulkPreviewLimit),
      };
    };

    const handlePreviewGeneratedForms = async () => {
      if (settings || !selectedProject) {
        return;
      }

      const formNameValue = (getValues('formName') || '').trim();
      const formDescriptionValue = (getValues('formDescription') || '').trim();
      const fieldsValue = getValues('fields') || [];
      const activeFormStructure = resolveActiveFormStructure(fieldsValue);
      if (!activeFormStructure) {
        return;
      }

      const normalizedBusinessRules = normalizeBusinessRules(businessRules);
      const normalizedFlkRules = normalizeFlkRules(flkRules);
      let normalizedDependentDisplayRules: FormViewDependencyRule[] = [];
      try {
        normalizedDependentDisplayRules = buildDependentDisplayRulesPayload();
      } catch (error) {
        setGlobalError(
          error instanceof Error ? error.message : 'Некорректные правила зависимого отображения',
        );
        return;
      }
      const generationPayloadBase = {
        createdBy: 'admin',
        projectId: selectedProject.id,
        formStructure: activeFormStructure,
        appearanceSettings: getValues('appearanceSettings') || {},
        viewSettings: {
          ...(getValues('viewSettings') || {}),
          dependentDisplayRules: normalizedDependentDisplayRules,
        },
        businessRules: normalizedBusinessRules,
        flkRules: normalizedFlkRules,
      };

      let generationPayload: BulkGenerateFormsParams;
      try {
        generationPayload = buildBulkGenerationPayload(
          generationPayloadBase,
          formNameValue,
          formDescriptionValue,
        );
      } catch (error) {
        setGlobalError(error instanceof Error ? error.message : 'Неверные параметры генерации');
        return;
      }

      setIsBulkPreviewLoading(true);
      try {
        const { data } = await ProjectsService.previewFormsBulk({
          generation: generationPayload,
          stopOnError: false,
        });
        setBulkPreview(data);
      } catch (error: any) {
        setGlobalError(error?.response?.data?.error || 'Не удалось получить предпросмотр');
      } finally {
        setIsBulkPreviewLoading(false);
      }
    };

    const isPlainObject = (value: unknown): value is Record<string, unknown> =>
      Boolean(value) && typeof value === 'object' && !Array.isArray(value);

    const compactRuleConfig = (
      config?: Record<string, unknown>,
    ): Record<string, unknown> | undefined => {
      if (!isPlainObject(config)) {
        return undefined;
      }

      const result = Object.entries(config).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value === null || value === undefined) {
          return acc;
        }
        if (typeof value === 'string' && !value.trim()) {
          return acc;
        }
        if (Array.isArray(value) && !value.length) {
          return acc;
        }
        if (isPlainObject(value) && !Object.keys(value).length) {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});

      if (!Object.keys(result).length) {
        return undefined;
      }
      return result;
    };

    const normalizeBusinessRules = (rules: FormBusinessRule[]): FormBusinessRule[] => {
      return rules
        .map((rule, index) => {
          const name = (rule.name || '').trim();
          const type = (rule.type || 'math').trim().toLowerCase();
          const expression = (rule.expression || '').trim();
          const targetColumn = (rule.targetColumn || '').trim();
          const compactConfig = compactRuleConfig(rule.config as Record<string, unknown>);

          return {
            ...rule,
            id: rule.id || `rule_${index + 1}`,
            name: name || `rule_${index + 1}`,
            type: type || 'math',
            priority:
              Number.isFinite(rule.priority) && Number(rule.priority) > 0
                ? Math.trunc(Number(rule.priority))
                : index + 1,
            ...(expression ? { expression } : {}),
            ...(targetColumn ? { targetColumn } : {}),
            enabled: rule.enabled !== false,
            ...(compactConfig ? { config: compactConfig } : {}),
          };
        })
        .filter(rule => Boolean(rule.name || rule.expression || rule.targetColumn || rule.config));
    };

    const normalizeFlkRules = (rules: FormFlkRule[]): FormFlkRule[] => {
      return rules
        .map((rule, index) => {
          const name = (rule.name || '').trim();
          const expression = (rule.expression || '').trim();
          const level = (rule.level || 'error').trim().toLowerCase();
          const scope = (rule.scope || 'cell').trim().toLowerCase();
          const targetColumn = (rule.targetColumn || '').trim();
          const message = (rule.message || '').trim();
          const compactConfig = compactRuleConfig(rule.config as Record<string, unknown>);

          return {
            ...rule,
            id: rule.id || `flk_${index + 1}`,
            name: name || `flk_${index + 1}`,
            expression,
            level: level === 'warning' ? 'warning' : 'error',
            scope: scope === 'form' ? 'form' : 'cell',
            ...(scope === 'form' ? {} : { targetColumn }),
            ...(message ? { message } : {}),
            priority:
              Number.isFinite(rule.priority) && Number(rule.priority) > 0
                ? Math.trunc(Number(rule.priority))
                : index + 1,
            enabled: rule.enabled !== false,
            ...(compactConfig ? { config: compactConfig } : {}),
          };
        })
        .filter(rule => Boolean(rule.name || rule.expression || rule.targetColumn || rule.message));
    };

    const getMapSize = (value: unknown): number => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return 0;
      }
      return Object.keys(value).length;
    };

    const validateBusinessRules = (rules: FormBusinessRule[]): string[] => {
      const errorsList: string[] = [];
      const allowedTypes = new Set(['math', 'aggregate', 'transfer', 'consolidation', 'python']);

      rules.forEach((rule, index) => {
        const ruleName = (rule.name || `rule_${index + 1}`).trim();
        const ruleType = (rule.type || '').trim().toLowerCase();
        const config = (rule.config || {}) as Record<string, unknown>;

        if (!ruleName) {
          errorsList.push(`Правило #${index + 1}: требуется название`);
        }
        if (!ruleType || !allowedTypes.has(ruleType)) {
          errorsList.push(`${ruleName}: неподдерживаемый тип "${rule.type || ''}"`);
          return;
        }

        if (ruleType === 'python') {
          const pythonExpression = String(
            rule.expression || config.script || config.expression || '',
          ).trim();
          if (!rule.targetColumn) {
            errorsList.push(`${ruleName}: требуется целевая колонка`);
          }
          if (!pythonExpression) {
            errorsList.push(`${ruleName}: требуется Python-выражение`);
          }
          return;
        }

        if (!rule.targetColumn && ruleType !== 'transfer' && ruleType !== 'consolidation') {
          errorsList.push(`${ruleName}: требуется целевая колонка`);
        }

        if (ruleType === 'math' && !(rule.expression || '').trim()) {
          errorsList.push(`${ruleName}: требуется выражение`);
        }

        if (ruleType === 'aggregate') {
          const fn = String(config.function || '')
            .trim()
            .toLowerCase();
          const sourceColumn = String(config.sourceColumn || '').trim();
          if (!fn) {
            errorsList.push(`${ruleName}: требуется агрегирующая функция`);
          }
          if (fn !== 'count' && !sourceColumn) {
            errorsList.push(`${ruleName}: для агрегирования требуется исходная колонка`);
          }
        }

        if (ruleType === 'transfer') {
          const targetTable = String(config.targetTable || '').trim();
          const columnMapSize = getMapSize(config.columnMap);
          if (!targetTable) {
            errorsList.push(`${ruleName}: требуется целевая таблица`);
          }
          if (columnMapSize === 0) {
            errorsList.push(`${ruleName}: требуется минимум одно сопоставление колонок`);
          }
        }

        if (ruleType === 'consolidation') {
          const targetTable = String(config.targetTable || '').trim();
          const keyMapSize = getMapSize(config.keyMap);
          const valueMapSize = getMapSize(config.valueMap);
          if (!targetTable) {
            errorsList.push(`${ruleName}: требуется целевая таблица`);
          }
          if (keyMapSize === 0) {
            errorsList.push(`${ruleName}: требуется минимум одно сопоставление ключей`);
          }
          if (valueMapSize === 0) {
            errorsList.push(`${ruleName}: требуется минимум одно сопоставление значений`);
          }
        }
      });

      return errorsList;
    };

    const validateFlkRules = (rules: FormFlkRule[]): string[] => {
      const errorsList: string[] = [];
      const allowedLevels = new Set(['error', 'warning']);
      const allowedScopes = new Set(['cell', 'form']);

      rules.forEach((rule, index) => {
        const ruleName = (rule.name || `flk_${index + 1}`).trim();
        const level = (rule.level || '').trim().toLowerCase();
        const scope = (rule.scope || '').trim().toLowerCase();

        if (!ruleName) {
          errorsList.push(`Правило ФЛК #${index + 1}: требуется название`);
        }
        if (!(rule.expression || '').trim()) {
          errorsList.push(`${ruleName}: требуется выражение`);
        }
        if (!allowedLevels.has(level)) {
          errorsList.push(`${ruleName}: уровень должен быть error или warning`);
        }
        if (!allowedScopes.has(scope)) {
          errorsList.push(`${ruleName}: область должна быть cell или form`);
        }
        if (scope !== 'form' && !(rule.targetColumn || '').trim()) {
          errorsList.push(`${ruleName}: для cell-правила требуется целевая колонка`);
        }
      });

      return errorsList;
    };

    const onSubmit: SubmitHandler<ProjectSettingsFormType> = async ({
      formDescription,
      formName,
      bulkFormNames,
      fields,
      appearanceSettings,
      viewSettings,
    }) => {
      if (!selectedProject) {
        return;
      }

      const names = checkNames(fields);

      fields.forEach((field, i) => {
        if (names?.[field.columnName] > 1) {
          setError(`fields.${i}.columnName`, { message: 'Столбец с таким именем существует' });
        }
      });

      const resolvedFormStructureRaw = resolveFormStructureWithReferences(fields);
      if (!resolvedFormStructureRaw) {
        return;
      }
      const resolvedFormStructure = resolvedFormStructureRaw.filter(
        (_, i) => !localFields[i].removed,
      );

      const parsedBusinessRules = normalizeBusinessRules(businessRules);
      const businessRuleErrors = validateBusinessRules(parsedBusinessRules);
      if (businessRuleErrors.length) {
        setGlobalError(`Проверка бизнес-правил: ${businessRuleErrors[0]}`);
        return;
      }
      const parsedFlkRules = normalizeFlkRules(flkRules);
      const flkRuleErrors = validateFlkRules(parsedFlkRules);
      if (flkRuleErrors.length) {
        setGlobalError(`Проверка ФЛК: ${flkRuleErrors[0]}`);
        return;
      }

      try {
        let res: CreateFormRes | null = null;
        let createdForms: FormType[] = [];
        const normalizedAppearanceSettings = appearanceSettings || {};
        let normalizedDependentDisplayRules: FormViewDependencyRule[] = [];
        try {
          normalizedDependentDisplayRules = buildDependentDisplayRulesPayload();
        } catch (error) {
          setGlobalError(
            error instanceof Error ? error.message : 'Некорректные правила зависимого отображения',
          );
          return;
        }
        const normalizedViewSettings = {
          ...(viewSettings || {}),
          dependentDisplayRules: normalizedDependentDisplayRules,
        };
        const hasMetadataChanges =
          settings &&
          (JSON.stringify(settings?.appearanceSettings || {}) !==
            JSON.stringify(normalizedAppearanceSettings) ||
            JSON.stringify(settings?.viewSettings || {}) !==
              JSON.stringify(normalizedViewSettings) ||
            JSON.stringify(settings?.businessRules || []) !== JSON.stringify(parsedBusinessRules) ||
            JSON.stringify(settings?.flkRules || []) !== JSON.stringify(parsedFlkRules));

        if (
          settings &&
          (settings?.formDescription !== formDescription ||
            settings?.formName !== formName ||
            hasMetadataChanges)
        ) {
          const { data } = await ProjectsService.updateForm({
            formName,
            formDescription,
            id: settings.id,
            appearanceSettings: normalizedAppearanceSettings,
            viewSettings: normalizedViewSettings,
            businessRules: parsedBusinessRules,
            flkRules: parsedFlkRules,
          });
          res = data;
        }

        const newRows: FieldType[] = [];
        const removeRows: FieldType[] = [];
        const editRows: FieldType[] = [];

        resolvedFormStructureRaw.forEach((row, i) => {
          const { isNew, removed } = localFields?.[i];
          if (isNew && !removed) {
            newRows.push(row);
          } else if (removed && !isNew) {
            removeRows.push(row);
          } else if (!isNew && !removed && settings) {
            const prev = settings?.formStructure?.[i];
            if (
              prev?.columnType !== row.columnType ||
              prev?.columnName !== row.columnName ||
              prev?.columnLabel !== row?.columnLabel ||
              prev?.referenceFormId !== row?.referenceFormId ||
              prev?.referenceFormKey !== row?.referenceFormKey ||
              prev?.referenceFormValue !== row?.referenceFormValue
            ) {
              editRows.push({
                ...row,
                newColumnName: row.columnName,
                columnName: prev?.columnName,
              });
            }
          }
        });

        if (newRows.length && settings) {
          const { data } = await ProjectsService.addColumnForm({
            id: settings.id,
            columns: newRows,
          });
          res = data;
        }

        if (editRows.length && settings) {
          const { data } = await ProjectsService.updateColumnForm({
            id: settings.id,
            columns: editRows,
          });
          res = data;
        }

        if (removeRows.length && settings) {
          const { data } = await ProjectsService.deleteColumnForm({
            id: settings.id,
            columns: removeRows,
          });
          res = data;
        }

        if (!settings) {
          const activeFormStructure = resolvedFormStructure;
          const basePayload = {
            formDescription,
            createdBy: 'admin',
            projectId: selectedProject.id,
            formStructure: activeFormStructure,
            appearanceSettings: appearanceSettings || {},
            viewSettings: normalizedViewSettings,
            businessRules: parsedBusinessRules,
            flkRules: parsedFlkRules,
          };
          if (bulkGenerationEnabled) {
            let generationPayload: BulkGenerateFormsParams;
            try {
              generationPayload = buildBulkGenerationPayload(
                basePayload,
                formName,
                formDescription,
              );
            } catch (error) {
              setGlobalError(
                error instanceof Error ? error.message : 'Неверные параметры генерации',
              );
              return;
            }

            const { data } = await ProjectsService.createFormsBulk({
              stopOnError: false,
              generation: generationPayload,
            });

            createdForms = data.created.map(mapResponseToForm);
            if (data.errors?.length) {
              setGlobalError(`Не удалось создать форм: ${data.errors.length}`);
            } else if (data.warnings?.length) {
              setGlobalSuccess(data.warnings[0]);
            }
          } else {
            const normalizedNames = parseBulkFormNames(bulkFormNames, formName);

            if (normalizedNames.length > 1) {
              const { data } = await ProjectsService.createFormsBulk({
                stopOnError: false,
                forms: normalizedNames.map(name => ({ ...basePayload, formName: name })),
              });

              createdForms = data.created.map(mapResponseToForm);
              if (data.errors?.length) {
                setGlobalError(`Не удалось создать форм: ${data.errors.length}`);
              }
            } else {
              const { data } = await ProjectsService.createForm({
                ...basePayload,
                formName: normalizedNames[0] || formName,
              });
              res = data;
              createdForms = [mapResponseToForm(data)];
            }
          }
        }
        if (res || createdForms.length) {
          const newForms = settings
            ? forms.map(el => {
                if (el.id === (res?.Id || res?.['ID'])) {
                  return mapResponseToForm(res as CreateFormRes);
                }
                return el;
              })
            : [...forms, ...createdForms];

          setForms(newForms);

          if (!settings && createdForms.length > 1) {
            setGlobalSuccess(`Создано форм: ${createdForms.length}`);
          }

          if (settings) {
            setTableData(tableContextDefaultState.tableData);
            setTableSettings(null);
            setSelectedForm(null);
          }

          onClose();
        }
      } catch (e) {
        if (e?.response?.data?.error) {
          return setGlobalError(e?.response?.data?.error);
        }
        setGlobalError('Не удалось сохранить изменения');
      }
    };

    const iconButtonProps: ButtonProps = {
      variant: darkMode ? 'grey' : 'greyLight',
      size: 'small',
      variable: 'icon',
      classNames: 'group w- aspect-square',
    };

    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)} className="flex h-full w-full flex-col">
          <div className="flex-shrink-0 border-b border-blue-400 px-4 pb-5">
            <Input
              {...register('formName', { required: emptyError })}
              label="Название таблицы"
              placeholder="Введите название таблицы"
              error={errors.formName?.message}
              containerClassNames="mb-5"
              variant={darkMode ? 'blue' : 'grey'}
            />
            <Input
              {...register('formDescription', { required: emptyError })}
              label="Описание таблицы"
              placeholder="Введите описание таблицы"
              error={errors.formDescription?.message}
              variant={darkMode ? 'blue' : 'grey'}
            />
            {!settings && (
              <div className="mt-5">
                <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                  Массовое создание форм
                </div>
                <div className="mb-2 flex items-center gap-2 text-xs dark:text-white">
                  <input
                    id="bulk-generation-enabled"
                    type="checkbox"
                    checked={bulkGenerationEnabled}
                    onChange={event => {
                      setBulkGenerationEnabled(event.target.checked);
                      setBulkPreview(null);
                    }}
                  />
                  <label htmlFor="bulk-generation-enabled">
                    Генерировать формы по измерениям (с предпросмотром)
                  </label>
                </div>

                {!bulkGenerationEnabled && (
                  <>
                    <textarea
                      {...register('bulkFormNames')}
                      rows={4}
                      placeholder="Одно имя формы на каждой строке"
                      className="font-gothampro-400 w-full rounded border border-violet-400 bg-white p-3 text-xs text-black transition-all placeholder:text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    />
                    <div className="font-gothampro-400 ml-1 mt-1 text-extra-xs text-grey-300">
                      Если заполнено, будет создано несколько форм с общей структурой.
                    </div>
                  </>
                )}

                {bulkGenerationEnabled && (
                  <div className="rounded border border-blue-400 p-3">
                    <div className="mb-2 text-xs opacity-80 dark:text-white">
                      Шаблоны: используйте токены вида <code>{'{{dim1.value}}'}</code>,{' '}
                      <code>{'{{dim1.id}}'}</code>, <code>{'{{dim1.column_name}}'}</code>. Для
                      одного измерения доступен токен <code>{'{{value}}'}</code>.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Шаблон имени формы"
                        value={bulkNameTemplate}
                        onChange={event => setBulkNameTemplate(event.target.value)}
                        placeholder="{{value}}"
                        variant={darkMode ? 'blue' : 'grey'}
                      />
                      <Input
                        label="Шаблон описания"
                        value={bulkDescriptionTemplate}
                        onChange={event => setBulkDescriptionTemplate(event.target.value)}
                        placeholder="{{value}}"
                        variant={darkMode ? 'blue' : 'grey'}
                      />
                      <Input
                        label="Лимит экземпляров"
                        value={bulkInstanceLimit}
                        onChange={event => setBulkInstanceLimit(event.target.value)}
                        placeholder="100"
                        variant={darkMode ? 'blue' : 'grey'}
                      />
                      <Input
                        label="Лимит предпросмотра"
                        value={bulkPreviewLimit}
                        onChange={event => setBulkPreviewLimit(event.target.value)}
                        placeholder="50"
                        variant={darkMode ? 'blue' : 'grey'}
                      />
                    </div>

                    <div className="mt-3 flex items-center">
                      <div className="text-xs opacity-80 dark:text-white">Измерения</div>
                      <Button
                        size="small"
                        variant="violetOutline"
                        classNames="ml-auto w-32"
                        onClick={event => {
                          event.preventDefault();
                          addBulkDimension();
                        }}
                      >
                        Добавить
                      </Button>
                    </div>

                    <div className="mt-2 max-h-44 overflow-y-auto rounded border border-grey-1000 p-2">
                      {bulkDimensions.map((dimension, index) => {
                        const selectedDirectoryId =
                          typeof dimension.directoryFormId === 'number'
                            ? dimension.directoryFormId
                            : 0;
                        const attributeOptions = [
                          { value: 'id', label: 'ID' },
                          ...(directoryAttributes[selectedDirectoryId] || []).map(attribute => ({
                            value: attribute.ColumnName,
                            label: attribute.ColumnLabel || attribute.ColumnName,
                          })),
                        ];

                        return (
                          <div key={dimension.id} className="mb-3 grid grid-cols-12 gap-2">
                            <div className="col-span-2">
                              <Input
                                label="Ключ"
                                value={dimension.key}
                                onChange={event =>
                                  updateBulkDimension(dimension.id, { key: event.target.value })
                                }
                                placeholder={`dim${index + 1}`}
                                variant={darkMode ? 'blue' : 'grey'}
                              />
                            </div>

                            <div className="col-span-3">
                              <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                                Измерение
                              </div>
                              <select
                                value={String(dimension.directoryFormId || '')}
                                onChange={event => {
                                  const directoryId = Number(event.target.value);
                                  updateBulkDimension(dimension.id, {
                                    directoryFormId:
                                      Number.isFinite(directoryId) && directoryId > 0
                                        ? directoryId
                                        : '',
                                    valueColumn: 'id',
                                    sortColumn: 'id',
                                  });
                                  if (directoryId > 0) {
                                    void loadDirectoryAttributes(directoryId);
                                  }
                                }}
                                className="h-11 w-full rounded border border-violet-400 bg-white px-3 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                              >
                                <option value="">Выберите измерение</option>
                                {projectDirectories.map(directory => (
                                  <option key={directory.id} value={directory.id}>
                                    {directory.alias}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-2">
                              <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                                Колонка значения
                              </div>
                              <select
                                value={dimension.valueColumn}
                                onChange={event =>
                                  updateBulkDimension(dimension.id, {
                                    valueColumn: event.target.value,
                                  })
                                }
                                className="h-11 w-full rounded border border-violet-400 bg-white px-3 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                              >
                                {attributeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-2">
                              <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                                Сортировка
                              </div>
                              <select
                                value={dimension.sortColumn}
                                onChange={event =>
                                  updateBulkDimension(dimension.id, {
                                    sortColumn: event.target.value,
                                  })
                                }
                                className="h-11 w-full rounded border border-violet-400 bg-white px-3 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                              >
                                {attributeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-2">
                              <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                                Направление
                              </div>
                              <select
                                value={dimension.sortDirection}
                                onChange={event =>
                                  updateBulkDimension(dimension.id, {
                                    sortDirection: event.target.value === 'desc' ? 'desc' : 'asc',
                                  })
                                }
                                className="h-11 w-full rounded border border-violet-400 bg-white px-3 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                              >
                                <option value="asc">ASC</option>
                                <option value="desc">DESC</option>
                              </select>
                            </div>

                            <div className="col-span-1 flex items-end">
                              <Button
                                size="small"
                                variant="greyLight"
                                classNames="w-full"
                                onClick={event => {
                                  event.preventDefault();
                                  removeBulkDimension(dimension.id);
                                }}
                              >
                                X
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        size="small"
                        variant="violet"
                        classNames="w-40"
                        onClick={event => {
                          event.preventDefault();
                          void handlePreviewGeneratedForms();
                        }}
                        disabled={isBulkPreviewLoading}
                      >
                        {isBulkPreviewLoading ? 'Загрузка...' : 'Предпросмотр'}
                      </Button>
                    </div>

                    {bulkPreview && (
                      <div className="mt-3 rounded border border-grey-1000 p-2">
                        <div className="mb-2 text-xs dark:text-white">
                          Всего экземпляров: {bulkPreview.total}, показано: {bulkPreview.displayed}
                          {bulkPreview.truncated ? ' (сокращено)' : ''}
                        </div>
                        {Boolean(bulkPreview.warnings?.length) && (
                          <div className="mb-2 text-extra-xs text-orange-100">
                            {bulkPreview.warnings?.[0]}
                          </div>
                        )}
                        <div className="max-h-40 overflow-y-auto text-extra-xs dark:text-white">
                          {bulkPreview.items.map(item => (
                            <div key={`bulk-preview-${item.index}`} className="mb-1">
                              {item.index + 1}. {item.formName} - {item.formDescription}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <BusinessRulesBuilder
              rules={businessRules}
              availableColumns={availableColumns}
              availableTables={availableTargetTables}
              onChange={setBusinessRules}
              mode="full"
            />
            <FlkRulesBuilder
              rules={flkRules}
              availableColumns={availableColumns}
              onChange={setFlkRules}
            />

            <div className="mt-5 rounded border border-blue-400 p-3">
              <div className="mb-3 text-xs opacity-70 dark:text-white">
                Внешний вид и отображение
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                    Пресет
                  </div>
                  <Controller
                    name="appearanceSettings.preset"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || 'default'}
                        options={appearancePresetOptions}
                        onChange={({ value }) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
                <Input
                  {...register('appearanceSettings.fontFamily')}
                  label="Шрифт"
                  placeholder="GothamPro"
                  variant={darkMode ? 'blue' : 'grey'}
                />
                <Input
                  {...register('appearanceSettings.fontSize', { valueAsNumber: true })}
                  type="number"
                  min={10}
                  max={32}
                  label="Размер шрифта"
                  variant={darkMode ? 'blue' : 'grey'}
                />
                <Input
                  {...register('appearanceSettings.primaryColor')}
                  type="color"
                  label="Основной цвет"
                  variant={darkMode ? 'blue' : 'grey'}
                />
                <Input
                  {...register('appearanceSettings.backgroundColor')}
                  type="color"
                  label="Цвет фона"
                  variant={darkMode ? 'blue' : 'grey'}
                />
              </div>
              <div className="mt-3">
                <div className="font-gothampro-400 mb-1 ml-4 whitespace-nowrap text-xs leading-3 text-grey-300">
                  Пользовательский CSS (для таблицы)
                </div>
                <textarea
                  {...register('appearanceSettings.customCss')}
                  rows={3}
                  placeholder="background-image: linear-gradient(180deg, #fff, #f6f7fb);"
                  className="font-gothampro-400 w-full rounded border border-violet-400 bg-white p-3 text-xs text-black transition-all placeholder:text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                />
              </div>
              <div className="mt-3 flex items-center gap-6 text-xs dark:text-white">
                <label className="flex items-center gap-2">
                  <input type="checkbox" {...register('viewSettings.hideEmptyCells')} />
                  Скрывать пустые ячейки
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" {...register('viewSettings.hideReferencedValues')} />
                  Скрывать ссылочные значения
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" {...register('viewSettings.businessRulesStopOnError')} />
                  Останавливать правила при первой ошибке
                </label>
              </div>

              <TotalsRowViewSettings
                label={watchedTotalsRowSettings?.label ?? 'Итоги'}
                position={(watchedTotalsRowSettings?.position ?? 'end') as FormTotalsRowPosition}
                onLabelChange={value => updateTotalsRowViewSettings({ label: value })}
                onPositionChange={value => updateTotalsRowViewSettings({ position: value })}
              />

              <div className="mt-3 rounded border border-blue-400 p-3">
                <div className="mb-3 flex items-center">
                  <div className="text-xs opacity-70 dark:text-white">
                    Зависимое отображение полей
                  </div>
                  <Button
                    size="small"
                    variant="violetOutline"
                    classNames="ml-auto w-32"
                    onClick={event => {
                      event.preventDefault();
                      addDependentDisplayRule();
                    }}
                  >
                    Добавить правило
                  </Button>
                </div>

                {!dependentDisplayRules.length && (
                  <div className="mb-2 text-extra-xs opacity-70 dark:text-white">
                    Правила зависимого отображения не настроены.
                  </div>
                )}

                {dependentDisplayRules.map(rule => {
                  const needsValues = operatorNeedsValues(rule.operator);

                  return (
                    <div key={rule.id} className="mb-2 grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <div className="mb-1 text-extra-xs opacity-70 dark:text-white">Цель</div>
                        <select
                          value={rule.targetColumn}
                          onChange={event =>
                            updateDependentDisplayRule(rule.id, {
                              targetColumn: event.target.value,
                            })
                          }
                          className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                        >
                          <option value="">Выберите колонку</option>
                          {availableColumns.map(column => (
                            <option key={`dep-target-${column}`} value={column}>
                              {column}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-3">
                        <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                          Источник
                        </div>
                        <select
                          value={rule.sourceColumn}
                          onChange={event =>
                            updateDependentDisplayRule(rule.id, {
                              sourceColumn: event.target.value,
                            })
                          }
                          className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                        >
                          <option value="">Выберите колонку</option>
                          {availableColumns.map(column => (
                            <option key={`dep-source-${column}`} value={column}>
                              {column}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-2">
                        <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                          Оператор
                        </div>
                        <select
                          value={rule.operator}
                          onChange={event =>
                            updateDependentDisplayRule(rule.id, {
                              operator: event.target.value as FormViewDependencyOperator,
                            })
                          }
                          className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                        >
                          {dependentRuleOperators.map(operator => (
                            <option key={`${rule.id}-${operator.value}`} value={operator.value}>
                              {operator.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-3">
                        <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                          Значения
                        </div>
                        <input
                          value={rule.valuesInput}
                          onChange={event =>
                            updateDependentDisplayRule(rule.id, { valuesInput: event.target.value })
                          }
                          disabled={!needsValues}
                          placeholder={needsValues ? 'значение1, значение2' : 'Не требуется'}
                          className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                        />
                      </div>

                      <div className="col-span-1 flex flex-col items-center justify-end gap-1">
                        <label className="text-extra-xs opacity-70 dark:text-white">Вкл</label>
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={event =>
                            updateDependentDisplayRule(rule.id, { enabled: event.target.checked })
                          }
                        />
                        <Button
                          size="small"
                          variant="greyLight"
                          classNames="w-full"
                          onClick={event => {
                            event.preventDefault();
                            removeDependentDisplayRule(rule.id);
                          }}
                        >
                          X
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {settings?.connection && (
              <Input
                label="База данных"
                value={settings.connection}
                disabled
                containerClassNames="mt-5"
                variant={darkMode ? 'blue' : 'grey'}
              />
            )}
          </div>

          <div className="mb-6 flex min-h-80 flex-1 flex-col p-4">
            {/* Заголовки таблицы */}
            <div className="font-gothampro-400 mb-4 grid grid-cols-7 items-center gap-4 text-center text-sm dark:text-white">
              <div className="col-span-2 flex items-center justify-center">Имя</div>
              <div className="col-span-2 flex items-center justify-center">Тип</div>
              <div className="col-span-2 flex items-center justify-center">Имя в БД</div>
              <div className="flex items-center justify-center">Действие</div>
            </div>

            {/* Контейнер с полями */}
            <div className="max-h-[400px] flex-1 overflow-y-auto">
              {localFields.map((field, i) => (
                <Fragment key={`field-${i}`}>
                  {!field.removed && (
                    <div className="mb-4 grid grid-cols-7 items-center gap-4 text-center">
                      {/* Поле "Имя" */}
                      <ToolTip label="Введите понятное и удобное для оператора название колонки. Можно использовать русские символы и пробелы">
                        <Input
                          containerClassNames="col-span-2"
                          placeholder="Введите имя"
                          error={errors.fields?.[i]?.columnLabel?.message}
                          variant={darkMode ? 'blue' : 'grey'}
                          {...register(`fields.${i}.columnLabel`, {
                            required: emptyError,
                            validate: val => {
                              if (val.length <= 25) {
                                return true;
                              }
                              return 'Максимум 25 символов';
                            },
                            onChange: e => {
                              const value = e.target.value;
                              const onlyWordsValue = value
                                .replace(/[^a-zа-яё_&0-9 ]/gi, '')
                                .replaceAll(' ', '_');
                              const transliterateValue = transliterate(onlyWordsValue);
                              setValue(`fields.${i}.columnName`, transliterateValue);

                              return e;
                            },
                          })}
                        />
                      </ToolTip>

                      {/* Поле "Тип" */}
                      <Controller
                        render={({ field }) => (
                          <Select
                            classNames="col-span-2"
                            value={
                              getTypeOptions(i).find(
                                el => el.value === settings?.formStructure?.[i]?.columnType,
                              )?.value || getTypeOptions(i)?.[0]?.value
                            }
                            options={getTypeOptions(i)}
                            onChange={({ value }) => {
                              clearErrors(`fields.${i}.columnType`);
                              field.onChange(value);
                            }}
                            error={errors.fields?.[i]?.columnType?.message}
                          />
                        )}
                        control={control}
                        name={`fields.${i}.columnType`}
                      />

                      {/* Поле "Имя в БД" */}
                      <ToolTip label="Введите английское наименование поля в таблице базы данных, не используйте спецсимволы и пробел">
                        <Input
                          containerClassNames="col-span-2"
                          placeholder="Введите имя в БД"
                          error={errors.fields?.[i]?.columnName?.message}
                          variant={darkMode ? 'blue' : 'grey'}
                          {...register(`fields.${i}.columnName`, {
                            required: emptyError,
                            validate: val => {
                              if (/^[a-zA-Z_0-9$]+$/.test(val)) {
                                return true;
                              }
                              return 'Только символы a-z|A-Z|0-9|_-';
                            },
                          })}
                        />
                      </ToolTip>

                      {/* Кнопка "Удалить" */}
                      <div className="flex items-center justify-center gap-1">
                        <Button {...iconButtonProps} onClick={e => handleRemoveRow(e, i)}>
                          <TrashIcon className="h-4 w-4 stroke-grey-500 group-hover:stroke-white" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center p-4">
            <Button
              variant="violet"
              classNames="mr-3"
              onClick={e => {
                e?.preventDefault();
                handleAddRow();
              }}
            >
              Добавить строку
            </Button>
            <Button variant="green" type="submit" disabled={isSubmitting} classNames="mr-3">
              Сохранить
            </Button>
            <Button variant="greyLight" classNames="mr-3" onClick={handleCancel}>
              Отменить
            </Button>
          </div>
        </form>
      </>
    );
  },
);
